import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';

import ExclamationMark from '@/assets/icons/exclamation-mark-circle';
import { URLS, createJurisdictionHeaders } from '@/data';
import { getGeocomplyClient } from '@/data/location/geocomply-common';
import { GeocomplyPayload } from '@/data/location/types';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { logger } from '@/utils/logging';
import { useMutation, useQuery } from '@tanstack/react-query';

const LOG_TAG = '[Casino Session]';
const GEO_TOKEN_FALLBACK_REFETCH_INTERVAL_MS = 59 * 60 * 1000;
const GEO_TOKEN_REFRESH_BUFFER_MS = 30 * 1000;
const GEO_TOKEN_MIN_REFETCH_INTERVAL_MS = 1000;
const GEO_TOKEN_MIN_VALIDITY_FOR_SESSION_MS = 10000;
const GEO_TOKEN_MIN_VALIDITY_FOR_CACHE_MS = 60 * 1000;

export type CasinoSessionResponse = {
    data: {
        session_id: string;
    };
};

export const getCasinoHeaders = () => {
    const socialCurrency = useSocialCurrencyStore.getState().activeCurrency;
    if (socialCurrency === SocialCurrency.COINS) {
        return { JURISDICTION: 'COIN-CASINO' };
    }
    return { JURISDICTION: 'CASH-CASINO' };
};

const fetchCasinoSession = async (geoPayload: GeocomplyPayload | undefined): Promise<CasinoSessionResponse> => {
    const socialCurrency = useSocialCurrencyStore.getState().activeCurrency;
    const isCoinsCurrency = socialCurrency === SocialCurrency.COINS;
    const geoToken = isCoinsCurrency ? 'BYPASS_TOKEN' : geoPayload?.geo_session_token;

    if (!geoToken) {
        throw new Error('GeoToken is required to fetch casino session');
    }

    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/casino/session`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            ...getCasinoHeaders(),
            'X-GeoToken-Id': geoToken,
            'Content-Type': 'application/json',
            Platform: Platform.OS,
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(`Failed to fetch casino session: ${response.statusText}`);
    }

    return data;
};

const renewCasinoSession = async (
    sessionId: string,
    geoPayload: GeocomplyPayload | undefined
): Promise<CasinoSessionResponse> => {
    const geoToken = geoPayload?.geo_session_token;

    if (!geoToken) {
        throw new Error('GeoToken is required to renew casino session');
    }

    if (!sessionId) {
        throw new Error('Session ID is required to renew casino session');
    }

    const response = await fetch(`${URLS.CHAMELON_API_URL}/auth/casino/update-geo-session`, {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            ...getCasinoHeaders(),
            'X-GeoToken-Id': geoToken,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId, geotoken: geoToken }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(`Failed to renew casino session: ${response.statusText}`);
    }

    return data;
};

export const getGeoToken = async () => {
    let payload: GeocomplyPayload | undefined;
    try {
        const geocomplyClient = getGeocomplyClient();

        const { decoded: cachedDecodedPayload } = await geocomplyClient.request({ readCache: true });

        if (hasGeoTokenMinValidity(cachedDecodedPayload, GEO_TOKEN_MIN_VALIDITY_FOR_CACHE_MS)) {
            payload = cachedDecodedPayload;
        } else {
            logger.debug(LOG_TAG, 'Cached geo token is near expiry, requesting a fresh token');
            const { decoded: freshDecodedPayload } = await geocomplyClient.request({ readCache: false });
            payload = freshDecodedPayload;
        }

        if (!payload.can_bet) {
            throw payload;
        }

        return payload;
    } catch (error) {
        logger.debug(LOG_TAG, 'geocomply error', `msg:${payload?.original_payload?.nodes?.error_message}`, error);
        throw payload;
    }
};

const getGeoTokenExpiresAtMs = (payload?: GeocomplyPayload): number | undefined => {
    const issuedAtTimestamp = payload?.original_payload?.nodes?.timestamp;
    const issuedAtMs = issuedAtTimestamp ? Date.parse(issuedAtTimestamp) : Number.NaN;
    const sessionTimeoutSeconds = Number(payload?.session_timeout);

    if (!Number.isFinite(issuedAtMs) || !Number.isFinite(sessionTimeoutSeconds) || sessionTimeoutSeconds <= 0) {
        return undefined;
    }

    return issuedAtMs + sessionTimeoutSeconds * 1000;
};

const getGeoTokenRefetchIntervalMs = (payload?: GeocomplyPayload): number => {
    const expiresAtMs = getGeoTokenExpiresAtMs(payload);

    if (!expiresAtMs) {
        return GEO_TOKEN_FALLBACK_REFETCH_INTERVAL_MS;
    }

    const refreshAtMs = expiresAtMs - GEO_TOKEN_REFRESH_BUFFER_MS;
    const msUntilRefresh = refreshAtMs - Date.now();

    return Math.max(msUntilRefresh, GEO_TOKEN_MIN_REFETCH_INTERVAL_MS);
};

const hasGeoTokenMinValidity = (payload: GeocomplyPayload | undefined, minValidityMs: number): boolean => {
    const expiresAtMs = getGeoTokenExpiresAtMs(payload);

    if (!expiresAtMs) {
        return false;
    }

    return expiresAtMs - Date.now() > minValidityMs;
};

const useCasinoGeoToken = (enabled: boolean) => {
    const isCashCurrency = useSocialCurrencyStore(state => state.activeCurrency === SocialCurrency.CASH);

    return useQuery<GeocomplyPayload | undefined>({
        queryKey: ['casino-geotoken'],
        queryFn: getGeoToken,
        enabled: isCashCurrency && enabled,
        staleTime: Infinity, //manage renew time manually based on token expiry
        refetchInterval: query => getGeoTokenRefetchIntervalMs(query?.state?.data),
        refetchIntervalInBackground: true,
        refetchOnReconnect: 'always',
        retry: 2,
    });
};

/**
 * Hook to preload getGeoToken
 */
export const usePrefetchGeoToken = (geocomplyClientReady: boolean) => {
    const { showInfoSheet } = useAlerts();
    const { t } = useTranslation();

    const geoTokenQuery = useCasinoGeoToken(geocomplyClientReady);

    const hasLocationFailure = geoTokenQuery.isError || (geoTokenQuery.isFetched && !geoTokenQuery.data?.can_bet);

    useEffect(() => {
        if (!hasLocationFailure) {
            return;
        }

        showInfoSheet({
            icon: <ExclamationMark />,
            title: t('location_failure'),
            description: t('error_subtitle'),
            buttonLabel: t('close'),
        });
    }, [hasLocationFailure, showInfoSheet, t]);

    return geoTokenQuery;
};

/**
 * Hook to renew casino session when geoToken expires during active gameplay
 */
export const useRenewCasinoSession = () => {
    return useMutation({
        mutationFn: ({ sessionId, geoPayload }: { sessionId: string; geoPayload: GeocomplyPayload | undefined }) =>
            renewCasinoSession(sessionId, geoPayload),
    });
};

/**
 * Hook to fetch casino session ID
 */
export const useCasinoSession = () => {
    const isCoinsCurrency = useSocialCurrencyStore(state => state.activeCurrency === SocialCurrency.COINS);

    const geoTokenQuery = useCasinoGeoToken(true);

    const geoPayload = geoTokenQuery.data;
    const geoToken = geoPayload?.geo_session_token;
    const isGeoTokenLoading = geoTokenQuery.isLoading;
    const hasValidGeoToken = hasGeoTokenMinValidity(geoPayload, GEO_TOKEN_MIN_VALIDITY_FOR_SESSION_MS);

    const isSessionQueryEnabled =
        isCoinsCurrency || (!!geoToken && !isGeoTokenLoading && geoPayload?.can_bet && hasValidGeoToken);

    const sessionQuery = useQuery<CasinoSessionResponse>({
        queryKey: ['casino-session', isCoinsCurrency ? 'coins' : 'cash'],
        queryFn: () => fetchCasinoSession(geoPayload),
        enabled: isSessionQueryEnabled,
    });

    const isLoading = isGeoTokenLoading || sessionQuery.isLoading;

    return {
        ...sessionQuery,
        isLoading,
    };
};
